import {
    Component,
    Element,
    Fragment,
    h,
    Host,
    Prop,
    Watch,
} from "@stencil/core";

/**
 * @slot (default) - Slot for the card image
 */
@Component({
    tag: "ux-iws-sf-card-image",
    styleUrl: "sf-card-image.scss",
    shadow: true,
})
export class SfCardImage {
    /** The host element of the component */
    @Element() host: HTMLUxIwsSfCardImageElement;

    /** Aspect ratio of the card image. */
    @Prop() aspectRatio: string = "auto";

    /** The source URL for the card image. This is used when no image slot is provided. */
    @Prop() src: string = "";

    /** The alt text for the card image. This is used when no image slot is provided. */
    @Prop() alt: string = "";

    /** The icon for the card image, used when no image slot is provided. */
    @Prop() icon: string = "";

    @Watch("aspectRatio")
    aspectRatioChanged() {
        // Update CSS variables when aspect ratio changes
        this.setAspectRatioCSSVars();
    }

    /** Validate and fix aspect ratio format */
    private fixAspectRatio = (value: string): string => {
        if (/^[0-9\s]+[/:][0-9\s]+$/.test(value)) {
            return value.replace(":", "/").replace(/[\s]+/g, "");
        }
        return "auto";
    };

    private setAspectRatioCSSVars = () => {
        this.host.style.setProperty(
            "--aspect-ratio",
            this.fixAspectRatio(this.aspectRatio)
        );
    };

    componentWillLoad() {
        // Set the aspect ratio based on the prop value
        this.setAspectRatioCSSVars();
    }

    render() {
        const hasImageSlot = !!this.host.querySelector("img");
        return (
            <Host>
                <figure class="container">
                    <slot />
                    {!hasImageSlot && (
                        <Fragment>
                            {/* Fallback image if no slot is provided */}
                            {this.src && (
                                <img src={this.src} alt={this.alt || ""} />
                            )}
                            {/* Fallback icon if no slot or imageSrc is provided */}
                            {this.icon && (
                                <ux-icon
                                    name={this.icon}
                                    aria-label={this.alt || undefined}
                                />
                            )}
                        </Fragment>
                    )}
                </figure>
            </Host>
        );
    }
}
